import React, { useState } from 'react';
import styled from 'styled-components';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 2rem;
  text-align: center;
  font-family: 'Cairo', sans-serif;
`;

const CalendarContainer = styled.div`
  margin-bottom: 2rem;
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const MonthYear = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
`;

const NavButton = styled.button`
  background: #3498db;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background 0.3s ease;

  &:hover {
    background: #2980b9;
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const DayHeader = styled.div`
  text-align: center;
  font-weight: 600;
  color: #7f8c8d;
  padding: 0.5rem;
  font-size: 0.9rem;
`;

const DayCell = styled.button<{ isAvailable: boolean; isSelected: boolean }>`
  padding: 0.8rem;
  border: 2px solid ${props => props.isSelected ? '#3498db' : '#e0e0e0'};
  background: ${props => props.isSelected ? '#3498db' : props.isAvailable ? 'white' : '#f8f9fa'};
  color: ${props => props.isSelected ? 'white' : props.isAvailable ? '#2c3e50' : '#bdc3c7'};
  border-radius: 8px;
  cursor: ${props => props.isAvailable ? 'pointer' : 'not-allowed'};
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    ${props => props.isAvailable && !props.isSelected && `
      border-color: #3498db;
      background: #f8f9fa;
    `}
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const TimeSlotContainer = styled.div`
  margin-bottom: 2rem;
`;

const TimeSlotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.5rem;
  margin-top: 1rem;
`;

const TimeSlot = styled.button<{ isSelected: boolean }>`
  padding: 0.8rem;
  border: 2px solid ${props => props.isSelected ? '#3498db' : '#e0e0e0'};
  background: ${props => props.isSelected ? '#3498db' : 'white'};
  color: ${props => props.isSelected ? 'white' : '#2c3e50'};
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    ${props => !props.isSelected && `
      border-color: #3498db;
      background: #f8f9fa;
    `}
  }
`;

const FormContainer = styled.div`
  margin-top: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #2c3e50;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.8rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.8rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  ${props => props.variant === 'primary' ? `
    background: #3498db;
    color: white;
    &:hover {
      background: #2980b9;
    }
  ` : `
    background: #ecf0f1;
    color: #2c3e50;
    &:hover {
      background: #d5dbdb;
    }
  `}

  &:disabled {
    background: #bdc3c7;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
  font-weight: 600;
`;

const BookingDetails = styled.div`
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  margin: 1rem 0;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

interface BookingData {
  name: string;
  email: string;
  phone: string;
  nationality: string;
  date: string;
  time: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  language: 'ar' | 'en';
}

const IeltsCalendarModalSimple: React.FC<Props> = ({ isOpen, onClose, language }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [step, setStep] = useState<'calendar' | 'form' | 'success'>('calendar');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<BookingData>({
    name: '',
    email: '',
    phone: '',
    nationality: '',
    date: '',
    time: ''
  });

  const content = {
    ar: {
      title: 'حجز موعد اختبار الآيلتس',
      selectDate: 'اختر التاريخ',
      selectTime: 'اختر الوقت',
      personalInfo: 'المعلومات الشخصية',
      name: 'الاسم الكامل',
      email: 'البريد الإلكتروني',
      phone: 'رقم الهاتف',
      nationality: 'الجنسية',
      back: 'رجوع',
      next: 'التالي',
      book: 'احجز الآن',
      cancel: 'إلغاء',
      success: 'تم الحجز بنجاح!',
      successMessage: 'تم حفظ بياناتك محلياً. سيتم التواصل معك قريباً.',
      close: 'إغلاق',
      bookingDetails: 'تفاصيل الحجز'
    },
    en: {
      title: 'Book IELTS Test Appointment',
      selectDate: 'Select Date',
      selectTime: 'Select Time',
      personalInfo: 'Personal Information',
      name: 'Full Name',
      email: 'Email Address',
      phone: 'Phone Number',
      nationality: 'Nationality',
      back: 'Back',
      next: 'Next',
      book: 'Book Now',
      cancel: 'Cancel',
      success: 'Booking Successful!',
      successMessage: 'Your booking has been saved locally. We will contact you soon.',
      close: 'Close',
      bookingDetails: 'Booking Details'
    }
  };

  const currentContent = content[language];

  // Generate time slots from 10:30 AM to 6:00 PM (every 30 minutes)
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 10; hour < 18; hour++) {
      if (hour === 10) {
        slots.push('10:30 AM');
      } else if (hour < 12) {
        slots.push(`${hour}:00 AM`);
        slots.push(`${hour}:30 AM`);
      } else if (hour === 12) {
        slots.push('12:00 PM');
        slots.push('12:30 PM');
      } else {
        const displayHour = hour - 12;
        slots.push(`${displayHour}:00 PM`);
        slots.push(`${displayHour}:30 PM`);
      }
    }
    slots.push('6:00 PM');
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Check if date is available (not Friday or Saturday, and from tomorrow onwards)
  const isDateAvailable = (date: Date) => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    
    const dayOfWeek = date.getDay();
    return date >= tomorrow && dayOfWeek !== 5 && dayOfWeek !== 6; // 5 = Friday, 6 = Saturday
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const handleDateSelect = (date: Date) => {
    if (isDateAvailable(date)) {
      setSelectedDate(date);
      setSelectedTime('');
    }
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
  };

  const handleNext = () => {
    if (selectedDate && selectedTime) {
      setFormData({
        ...formData,
        date: selectedDate.toISOString().split('T')[0],
        time: selectedTime
      });
      setStep('form');
    }
  };

  const handleBack = () => {
    setStep('calendar');
  };

  const handleInputChange = (field: keyof BookingData, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    try {
      // Save to localStorage as backup
      const bookingData = {
        ...formData,
        timestamp: new Date().toISOString(),
        status: 'confirmed',
        id: Date.now().toString()
      };
      
      // Get existing bookings
      const existingBookings = JSON.parse(localStorage.getItem('ielts-bookings') || '[]');
      existingBookings.push(bookingData);
      
      // Save updated bookings
      localStorage.setItem('ielts-bookings', JSON.stringify(existingBookings));
      
      console.log('Booking saved to localStorage:', bookingData);
      setStep('success');
      
    } catch (error) {
      console.error('Error submitting booking:', error);
      alert('Error submitting booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      nationality: '',
      date: '',
      time: ''
    });
    setSelectedDate(null);
    setSelectedTime('');
    setStep('calendar');
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  const calendarDays = generateCalendarDays();
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <ModalOverlay onClick={handleBackdropClick}>
      <ModalContent>
        <ModalTitle>{currentContent.title}</ModalTitle>
        
        {step === 'success' && (
          <>
            <SuccessMessage>
              <h3>{currentContent.success}</h3>
              <p>{currentContent.successMessage}</p>
            </SuccessMessage>
            
            <BookingDetails>
              <h4>{currentContent.bookingDetails}</h4>
              <DetailRow>
                <strong>{currentContent.name}:</strong>
                <span>{formData.name}</span>
              </DetailRow>
              <DetailRow>
                <strong>{currentContent.email}:</strong>
                <span>{formData.email}</span>
              </DetailRow>
              <DetailRow>
                <strong>{currentContent.phone}:</strong>
                <span>{formData.phone}</span>
              </DetailRow>
              <DetailRow>
                <strong>Date:</strong>
                <span>{formData.date}</span>
              </DetailRow>
              <DetailRow>
                <strong>Time:</strong>
                <span>{formData.time}</span>
              </DetailRow>
            </BookingDetails>
            
            <ButtonContainer>
              <Button variant="primary" onClick={handleClose}>
                {currentContent.close}
              </Button>
            </ButtonContainer>
          </>
        )}
        
        {step === 'calendar' && (
          <>
            <CalendarContainer>
              <CalendarHeader>
                <NavButton 
                  onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))}
                >
                  ←
                </NavButton>
                <MonthYear>
                  {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                </MonthYear>
                <NavButton 
                  onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))}
                >
                  →
                </NavButton>
              </CalendarHeader>
              
              <CalendarGrid>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <DayHeader key={day}>{day}</DayHeader>
                ))}
                {calendarDays.map((date, index) => (
                  <DayCell
                    key={index}
                    isAvailable={isDateAvailable(date)}
                    isSelected={selectedDate?.toDateString() === date.toDateString()}
                    onClick={() => handleDateSelect(date)}
                    disabled={!isDateAvailable(date)}
                  >
                    {date.getDate()}
                  </DayCell>
                ))}
              </CalendarGrid>
            </CalendarContainer>

            {selectedDate && (
              <TimeSlotContainer>
                <h4>{currentContent.selectTime}</h4>
                <TimeSlotGrid>
                  {timeSlots.map(time => (
                    <TimeSlot
                      key={time}
                      isSelected={selectedTime === time}
                      onClick={() => handleTimeSelect(time)}
                    >
                      {time}
                    </TimeSlot>
                  ))}
                </TimeSlotGrid>
              </TimeSlotContainer>
            )}

            <ButtonContainer>
              <Button variant="secondary" onClick={handleClose}>
                {currentContent.cancel}
              </Button>
              <Button 
                variant="primary" 
                onClick={handleNext}
                disabled={!selectedDate || !selectedTime}
              >
                {currentContent.next}
              </Button>
            </ButtonContainer>
          </>
        )}

        {step === 'form' && (
          <FormContainer>
            <h3>{currentContent.personalInfo}</h3>
            
            <FormGroup>
              <Label>{currentContent.name} *</Label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>{currentContent.email} *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>{currentContent.phone} *</Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>{currentContent.nationality} *</Label>
              <Select
                value={formData.nationality}
                onChange={(e) => handleInputChange('nationality', e.target.value)}
                required
              >
                <option value="">Select Nationality</option>
                <option value="Kuwait">Kuwait</option>
                <option value="Saudi Arabia">Saudi Arabia</option>
                <option value="UAE">UAE</option>
                <option value="Qatar">Qatar</option>
                <option value="Bahrain">Bahrain</option>
                <option value="Oman">Oman</option>
                <option value="Other">Other</option>
              </Select>
            </FormGroup>

            <ButtonContainer>
              <Button variant="secondary" onClick={handleBack}>
                {currentContent.back}
              </Button>
              <Button 
                variant="primary" 
                onClick={handleSubmit}
                disabled={!formData.name || !formData.email || !formData.phone || !formData.nationality || isSubmitting}
              >
                {isSubmitting ? 'Booking...' : currentContent.book}
              </Button>
            </ButtonContainer>
          </FormContainer>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default IeltsCalendarModalSimple;
